import { type KeyboardEvent, type PointerEvent, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, errorMessage, patch, put } from '../../api/client';
import type { BikeModel, Media } from '../../api/types';
import { ACCEPTED_IMAGES, uploadImage } from '../../api/upload';
import { IconImage, IconPlus, IconTrash } from '../../components/icons';
import { ImageSlot } from '../../components/media';
import { DragHandle, SortableList } from '../../components/sortable';
import { Field, useFeedback } from '../../components/ui';
import { SaveBar } from './SaveBar';
import { useDraft, useUnloadGuard } from '../../hooks';
import { useT } from '../../i18n';
import { LIMITS, type TabProps } from './shared';

interface Spot {
  uid: string;
  name: string;
  x: number;
  y: number;
  media: Media | null;
}
interface Form {
  photo: Media | null;
  copy: string[];
  spots: Spot[];
}

const fromModel = (m: BikeModel): Form => ({
  photo: m.especialesMedia,
  copy: m.especialesCopy.length ? m.especialesCopy : [''],
  spots: m.specials.map((s) => ({ uid: s.id, name: s.name, x: s.x, y: s.y, media: s.media })),
});
const round = (n: number) => Math.round(n * 100) / 100;
const clamp = (n: number) => Math.min(100, Math.max(0, n));
const partA = (f: Form) => JSON.stringify([f.photo?.id ?? null, f.copy.map((p) => p.trim()).filter(Boolean)]);
const partB = (f: Form) => JSON.stringify(f.spots.map((s) => [s.name, round(s.x), round(s.y), s.media?.id ?? null]));

export function SpecialsTab({ model, onSaved, onDirty }: TabProps) {
  const { t } = useT();
  const initial = useMemo(() => fromModel(model), [model]);
  const { draft: f, setDraft: setF, dirty, reset, markSaved } = useDraft<Form>(initial, (a, b) => partA(a) === partA(b) && partB(a) === partB(b));
  const [active, setActive] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const { toast } = useFeedback();
  const dirtyA = partA(f) !== partA(initial);
  const dirtyB = partB(f) !== partB(initial);
  useUnloadGuard(dirty);
  useEffect(() => onDirty(dirty), [dirty, onDirty]);

  const setSpot = (uid: string, patchSpot: Partial<Spot>) => setF((p) => ({ ...p, spots: p.spots.map((s) => (s.uid === uid ? { ...s, ...patchSpot } : s)) }));

  const save = async () => {
    const submitted = f;
    setBusy(true);
    setError(null);
    // Son dos pedidos: si el segundo falla, lo que guardó el primero no se pierde de vista.
    let saved: BikeModel | undefined;
    try {
      if (dirtyA) {
        saved = (
          await patch<{ model: BikeModel }>(`/admin/models/${model.id}`, {
            especialesMediaId: f.photo?.id ?? null,
            especialesCopy: f.copy.map((p) => p.trim()).filter(Boolean),
          })
        ).model;
      }
      if (dirtyB) {
        saved = (
          await put<{ model: BikeModel }>(
            `/admin/models/${model.id}/specials`,
            f.spots.map((s) => ({ name: s.name.trim(), x: round(s.x), y: round(s.y), mediaId: s.media?.id ?? null })),
          )
        ).model;
      }
      if (saved) onSaved(saved);
      markSaved(submitted);
      toast(t('Especiales guardados'));
    } catch (e) {
      if (saved) onSaved(saved);
      setError(e instanceof ApiError ? e : new ApiError(0, errorMessage(e)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack">
      <section className="panel panel-pad stack">
        <div>
          <h2 className="section-title">{t('Puntos sobre la moto')}</h2>
          <p className="sub" style={{ margin: 0 }}>
            {t('Haz clic sobre la foto para agregar un punto y arrástralo para moverlo. El número sale del orden de la lista.')}
          </p>
        </div>
        {f.photo ? (
          <HotspotEditor
            photo={f.photo}
            spots={f.spots}
            active={active}
            onSelect={setActive}
            onMove={(uid, x, y) => setSpot(uid, { x, y })}
            onAdd={(x, y) => {
              if (f.spots.length >= LIMITS.specials) return toast(t('Máximo {max} especiales', { max: LIMITS.specials }), 'error');
              const spot: Spot = { uid: crypto.randomUUID(), name: '', x, y, media: null };
              setF((p) => ({ ...p, spots: [...p.spots, spot] }));
              setActive(spot.uid);
              requestAnimationFrame(() => document.getElementById(`spot-name-${spot.uid}`)?.focus());
            }}
          />
        ) : (
          <div className="notice warn">{t('Primero sube la foto de especiales (abajo): los puntos se ubican sobre ella.')}</div>
        )}

        {f.spots.length > 0 && (
          <SortableList items={f.spots} getId={(s) => s.uid} onReorder={(spots) => setF((p) => ({ ...p, spots }))} className="stack" >
            {(s, i, handle) => (
              <div className={`special-row ${active === s.uid ? 'active' : ''}`} onClick={() => setActive(s.uid)}>
                <DragHandle {...handle} />
                <span className="num">{String(i + 1).padStart(2, '0')}</span>
                <input
                  id={`spot-name-${s.uid}`}
                  className="input"
                  placeholder={t('Nombre del especial (p. ej. Tablero digital)')}
                  maxLength={LIMITS.specialName}
                  value={s.name}
                  onChange={(e) => setSpot(s.uid, { name: e.target.value })}
                  onFocus={() => setActive(s.uid)}
                />
                <DetailPhoto media={s.media} onChange={(m) => setSpot(s.uid, { media: m })} />
                <button className="btn btn-ghost btn-icon" title={t('Quitar')} onClick={() => setF((p) => ({ ...p, spots: p.spots.filter((x) => x.uid !== s.uid) }))}>
                  <IconTrash />
                </button>
              </div>
            )}
          </SortableList>
        )}
      </section>

      <div className="grid grid-2" style={{ alignItems: 'start' }}>
        <section className="panel panel-pad stack">
          <h2 className="section-title">{t('Foto de especiales')}</h2>
          <ImageSlot
            media={f.photo}
            onChange={(m) => setF((p) => ({ ...p, photo: m }))}
            aspect={f.photo?.width && f.photo.height ? `${f.photo.width} / ${f.photo.height}` : '3 / 2'}
            hint={t('La moto sobre fondo blanco. Si cambias la foto, revisa que los puntos sigan en su lugar.')}
          />
        </section>
        <section className="panel panel-pad stack">
          <h2 className="section-title">{t('Texto de la sección')}</h2>
          {f.copy.map((p, i) => (
            <Field key={i} label={t('Párrafo {n}', { n: i + 1 })} count={{ value: p, max: LIMITS.especialesParagraph }}>
              {(id) => (
                <div className="row" style={{ alignItems: 'flex-start' }}>
                  <textarea id={id} className="textarea" rows={5} value={p} onChange={(e) => setF((x) => ({ ...x, copy: x.copy.map((c, j) => (j === i ? e.target.value : c)) }))} />
                  {f.copy.length > 1 && (
                    <button className="btn btn-ghost btn-icon" title={t('Quitar párrafo')} onClick={() => setF((x) => ({ ...x, copy: x.copy.filter((_, j) => j !== i) }))}>
                      <IconTrash />
                    </button>
                  )}
                </div>
              )}
            </Field>
          ))}
          {f.copy.length < LIMITS.especialesParagraphs && (
            <div>
              <button className="btn btn-sm" onClick={() => setF((x) => ({ ...x, copy: [...x.copy, ''] }))}>
                <IconPlus /> {t('Párrafo')}
              </button>
            </div>
          )}
        </section>
      </div>

      <SaveBar dirty={dirty} busy={busy} error={error} onSave={save} onReset={reset} />
    </div>
  );
}

function HotspotEditor({
  photo,
  spots,
  active,
  onSelect,
  onMove,
  onAdd,
}: {
  photo: Media;
  spots: Spot[];
  active: string | null;
  onSelect: (uid: string) => void;
  onMove: (uid: string, x: number, y: number) => void;
  onAdd: (x: number, y: number) => void;
}) {
  const { t } = useT();
  const box = useRef<HTMLDivElement>(null);
  const dragging = useRef<string | null>(null);

  const position = (e: PointerEvent) => {
    const r = box.current!.getBoundingClientRect();
    return { x: round(clamp(((e.clientX - r.left) / r.width) * 100)), y: round(clamp(((e.clientY - r.top) / r.height) * 100)) };
  };

  const onKey = (e: KeyboardEvent, s: Spot) => {
    const step = e.shiftKey ? 2 : 0.5;
    const d = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[e.key];
    if (!d) return;
    e.preventDefault();
    onMove(s.uid, round(clamp(s.x + d[0]!)), round(clamp(s.y + d[1]!)));
  };

  return (
    <div
      ref={box}
      className="hotspot-editor"
      onPointerDown={(e) => {
        if ((e.target as HTMLElement).closest('.hotspot')) return;
        const { x, y } = position(e);
        onAdd(x, y);
      }}
    >
      <img src={photo.url} alt={t('Foto de especiales')} draggable={false} />
      {spots.map((s, i) => (
        <button
          key={s.uid}
          type="button"
          className={`hotspot ${active === s.uid ? 'active' : ''}`}
          style={{ left: `${s.x}%`, top: `${s.y}%` }}
          title={s.name || t('Punto {n}', { n: i + 1 })}
          aria-label={t('{name} ({n}): x {x} %, y {y} %. Flechas para mover.', { name: s.name || t('Punto'), n: i + 1, x: s.x, y: s.y })}
          onPointerDown={(e) => {
            e.stopPropagation();
            (e.target as HTMLElement).setPointerCapture(e.pointerId);
            dragging.current = s.uid;
            onSelect(s.uid);
          }}
          onPointerMove={(e) => {
            if (dragging.current !== s.uid) return;
            const { x, y } = position(e);
            onMove(s.uid, x, y);
          }}
          onPointerUp={() => (dragging.current = null)}
          onKeyDown={(e) => onKey(e, s)}
          onFocus={() => onSelect(s.uid)}
        >
          {String(i + 1).padStart(2, '0')}
        </button>
      ))}
    </div>
  );
}

/** Foto de detalle del especial (la que aparece en el tooltip del sitio). */
function DetailPhoto({ media, onChange }: { media: Media | null; onChange: (m: Media | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const { toast } = useFeedback();
  const { t } = useT();
  return (
    <>
      <button
        type="button"
        className="mini"
        title={media ? t('Cambiar foto de detalle') : t('Agregar foto de detalle')}
        onClick={(e) => {
          e.stopPropagation();
          input.current?.click();
        }}
      >
        {busy ? <span className="spinner" style={{ width: 16, height: 16 }} /> : media ? <img src={media.url} alt="" /> : <IconImage width={18} />}
      </button>
      <input
        ref={input}
        type="file"
        accept={ACCEPTED_IMAGES}
        hidden
        onChange={async (e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (!file) return;
          setBusy(true);
          try {
            onChange(await uploadImage(file));
          } catch (err) {
            toast(errorMessage(err), 'error');
          } finally {
            setBusy(false);
          }
        }}
      />
    </>
  );
}
